<template>
  <div class="data-dashboard">

    <!-- KPI Cards -->
    <div class="kpi-section">
      <div class="kpi-card">
        <div class="kpi-header">
          <h3>Total Payments</h3>
          <span class="kpi-trend positive">+12.5%</span>
        </div>
        <div class="kpi-value">{{ formatNumber(totals.total_payments) }}</div>
        <div class="kpi-subtitle">{{ selectedTimeRange }} day period</div>
      </div>
      
      <div class="kpi-card">
        <div class="kpi-header">
          <h3>Payment Volume</h3>
          <span class="kpi-trend positive">+8.3%</span>
        </div>
        <div class="kpi-value">{{ formatCurrency(totals.sum_payments) }}</div>
        <div class="kpi-subtitle">Total transaction value</div>
      </div>
      
      <div class="kpi-card">
        <div class="kpi-header">
          <h3>Gift Cards</h3>
          <span class="kpi-trend neutral">+2.1%</span>
        </div>
        <div class="kpi-value">{{ formatNumber(totals.total_gift_cards) }}</div>
        <div class="kpi-subtitle">Active gift cards</div>
      </div>
      
      <div class="kpi-card">
        <div class="kpi-header">
          <h3>Gift Card Value</h3>
          <span class="kpi-trend positive">+15.7%</span>
        </div>
        <div class="kpi-value">{{ formatCurrency(totals.sum_gift_cards) }}</div>
        <div class="kpi-subtitle">Outstanding balance</div>
      </div>
    </div>

    <!-- Charts Section (Hidden) -->
    <div class="charts-section" v-if="false">
      <!-- Payment Trends Chart -->
      <div class="chart-container">
        <div class="chart-header">
          <h3>Payment Trends</h3>
          <div class="chart-controls">
            <button 
              v-for="period in ['Daily', 'Weekly', 'Monthly']" 
              :key="period"
              @click="setChartPeriod(period)"
              :class="['chart-btn', { active: chartPeriod === period }]"
            >
              {{ period }}
            </button>
          </div>
        </div>
        <div ref="paymentTrendsChart" class="chart-area"></div>
      </div>

      <!-- Payment Distribution Chart -->
      <div class="chart-container">
        <div class="chart-header">
          <h3>Payment Distribution</h3>
        </div>
        <div ref="paymentDistributionChart" class="chart-area"></div>
      </div>
    </div>

    <!-- Transaction Status Chart (Hidden) -->
    <div class="charts-section" v-if="false">
      <div class="chart-container full-width">
        <div class="chart-header">
          <h3>Transaction Status Overview</h3>
        </div>
        <div ref="transactionStatusChart" class="chart-area"></div>
      </div>
    </div>

    <!-- Recent Transactions Table -->
    <div class="table-section">
      <div class="table-header">
        <h3>Recent Transactions</h3>
      </div>
      <div class="table-container">
        <table class="transactions-table">
          <thead>
            <tr>
              <th>Transaction ID</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Type</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="transaction in recentTransactions" :key="transaction.id">
              <td class="transaction-id">{{ transaction.reference }}</td>
              <td class="amount">{{ formatCurrency(transaction.amount) }}</td>
              <td>
                <span :class="['status-badge', transaction.status]">
                  {{ transaction.status }}
                </span>
              </td>
              <td>{{ transaction.type }}</td>
              <td>{{ formatDate(transaction.created_at) }}</td>
              <td>
                <a :href="'/transactions/'+transaction.uuid" target="_blank" class="action-btn">View</a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import Plotly from 'plotly.js-dist'

export default {
  data() {
    return {
      processing: false,
      selectedTimeRange: 30,
      selectedStatus: 'all',
      selectedAmountRange: 'all',
      chartPeriod: 'Daily',
      agency: {},
      totals: {
        total_gift_cards: 0,
        sum_gift_cards: 0,
        total_payments: 0,
        sum_payments: 0
      },
      recentTransactions: []
    }
  },

  mounted() {
    this.getSettings();
    this.getTotals();
    this.$nextTick(() => {
      this.initializeCharts();
    });
  },

  methods: {
    formatNumber(num) {
      return num ? num.toLocaleString() : '0';
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(amount || 0);
    },

    formatDate(date) {
      return new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    },

    onTimeRangeChange() {
      this.refreshData();
    },

    onStatusChange() {
      this.refreshData();
    },

    onAmountRangeChange() {
      this.refreshData();
    },

    setChartPeriod(period) {
      this.chartPeriod = period;
      this.updatePaymentTrendsChart();
    },

    refreshData() {
      this.processing = true;
      this.getTotals();
      this.updateCharts();
      setTimeout(() => {
        this.processing = false;
      }, 1000);
    },

    getSettings() {
      this.$http.get('/settings')
      .then(response => {
        if(response && response.data) {
          this.agency = response.data;
        }
      })
    },

    getTotals() {
      this.$http.get('/gift_cards/total')
      .then(response => {
        if(response && response.status) {
          this.totals.total_gift_cards = response.data;
        }
      })

      this.$http.get('/gift_cards/total?format=sum')
      .then(response => {
        if(response && response.status) {
          this.totals.sum_gift_cards = response.data;
        }
      })

      this.$http.get('/payments/total')
      .then(response => {
        if(response && response.status) {
          this.totals.total_payments = response.data;
        }
      })

      this.$http.get('/payments/total?format=sum')
      .then(response => {
        if(response && response.status) {
          this.totals.sum_payments = response.data;
        }
      })
    },

    getTransactions() {
      this.$http.get('/transactions')
      .then(response => {
        if(response && response.data && response.data.length > 0) {
          this.recentTransactions = response.data;
        }
      })
    },

    initializeCharts() {
      this.createPaymentTrendsChart();
      this.createPaymentDistributionChart();
      this.createTransactionStatusChart();
    },

    updateCharts() {
      this.updatePaymentTrendsChart();
      this.updatePaymentDistributionChart();
      this.updateTransactionStatusChart();
    },

    createPaymentTrendsChart() {
      const dates = this.generateDateRange(this.selectedTimeRange);
      const payments = this.generateSampleData(dates.length, 1000, 5000);
      const giftCards = this.generateSampleData(dates.length, 500, 2000);

      const data = [
        {
          x: dates,
          y: payments,
          type: 'scatter',
          mode: 'lines+markers',
          name: 'Payments',
          line: { color: '#1C7AB1', width: 3 },
          marker: { size: 6 }
        },
        {
          x: dates,
          y: giftCards,
          type: 'scatter',
          mode: 'lines+markers',
          name: 'Gift Cards',
          line: { color: '#28a745', width: 3 },
          marker: { size: 6 }
        }
      ];

      const layout = {
        paper_bgcolor: 'rgba(0,0,0,0)',
        plot_bgcolor: 'rgba(0,0,0,0)',
        font: { color: '#ffffff', family: 'Inter, sans-serif' },
        xaxis: {
          gridcolor: '#2a2a2a',
          linecolor: '#2a2a2a',
          tickcolor: '#2a2a2a'
        },
        yaxis: {
          gridcolor: '#2a2a2a',
          linecolor: '#2a2a2a',
          tickcolor: '#2a2a2a'
        },
        legend: {
          orientation: 'h',
          y: -0.2
        },
        margin: { t: 20, r: 20, b: 60, l: 60 }
      };

      const config = {
        displayModeBar: false,
        responsive: true
      };

      Plotly.newPlot(this.$refs.paymentTrendsChart, data, layout, config);
    },

    createPaymentDistributionChart() {
      const data = [{
        values: [65, 25, 10],
        labels: ['Completed', 'Pending', 'Failed'],
        type: 'pie',
        marker: {
          colors: ['#28a745', '#ffc107', '#dc3545']
        },
        textinfo: 'label+percent',
        textposition: 'outside',
        textfont: { color: '#ffffff' }
      }];

      const layout = {
        paper_bgcolor: 'rgba(0,0,0,0)',
        plot_bgcolor: 'rgba(0,0,0,0)',
        font: { color: '#ffffff', family: 'Inter, sans-serif' },
        showlegend: false,
        margin: { t: 20, r: 20, b: 20, l: 20 }
      };

      const config = {
        displayModeBar: false,
        responsive: true
      };

      Plotly.newPlot(this.$refs.paymentDistributionChart, data, layout, config);
    },

    createTransactionStatusChart() {
      const categories = ['0-100', '100-1K', '1K-10K', '10K+'];
      const completed = [120, 85, 45, 12];
      const pending = [25, 15, 8, 3];
      const failed = [8, 5, 2, 1];

      const data = [
        {
          x: categories,
          y: completed,
          name: 'Completed',
          type: 'bar',
          marker: { color: '#28a745' }
        },
        {
          x: categories,
          y: pending,
          name: 'Pending',
          type: 'bar',
          marker: { color: '#ffc107' }
        },
        {
          x: categories,
          y: failed,
          name: 'Failed',
          type: 'bar',
          marker: { color: '#dc3545' }
        }
      ];

      const layout = {
        paper_bgcolor: 'rgba(0,0,0,0)',
        plot_bgcolor: 'rgba(0,0,0,0)',
        font: { color: '#ffffff', family: 'Inter, sans-serif' },
        xaxis: {
          title: 'Transaction Amount Range',
          gridcolor: '#2a2a2a',
          linecolor: '#2a2a2a',
          tickcolor: '#2a2a2a'
        },
        yaxis: {
          title: 'Number of Transactions',
          gridcolor: '#2a2a2a',
          linecolor: '#2a2a2a',
          tickcolor: '#2a2a2a'
        },
        barmode: 'group',
        legend: {
          orientation: 'h',
          y: -0.2
        },
        margin: { t: 20, r: 20, b: 80, l: 60 }
      };

      const config = {
        displayModeBar: false,
        responsive: true
      };

      Plotly.newPlot(this.$refs.transactionStatusChart, data, layout, config);
    },

    updatePaymentTrendsChart() {
      if (this.$refs.paymentTrendsChart) {
        this.createPaymentTrendsChart();
      }
    },

    updatePaymentDistributionChart() {
      if (this.$refs.paymentDistributionChart) {
        this.createPaymentDistributionChart();
      }
    },

    updateTransactionStatusChart() {
      if (this.$refs.transactionStatusChart) {
        this.createTransactionStatusChart();
      }
    },

    generateDateRange(days) {
      const dates = [];
      for (let i = days - 1; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        dates.push(date.toISOString().split('T')[0]);
      }
      return dates;
    },

    generateSampleData(length, min, max) {
      return Array.from({ length }, () => 
        Math.floor(Math.random() * (max - min + 1)) + min
      );
    }
  }
}
</script>

<style scoped>
.data-dashboard {
  min-height: 100vh;
  color: #333;
  font-family: 'Nunito', sans-serif;
  padding: 0;
  margin: 0;
}

/* Header Styles */
.dashboard-header {
  background: linear-gradient(135deg, #1C7AB1 0%, #0d5a8a 100%);
  border-bottom: 1px solid #e9ecef;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 10px rgba(28, 122, 177, 0.1);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;
}

.dashboard-title {
  font-size: 2.5rem;
  font-weight: 800;
  margin: 0;
  color: #ffffff;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.dashboard-subtitle {
  color: rgba(255, 255, 255, 0.9);
  margin: 0.5rem 0 0 0;
  font-size: 1.1rem;
  font-weight: 400;
}

.user-section {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.user-info {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.user-name {
  font-weight: 600;
  font-size: 1.1rem;
  color: #ffffff;
}

.user-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
}

.status-indicator {
  width: 8px;
  height: 8px;
  background: #28a745;
  border-radius: 50%;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
}

/* Filters Section */
.filters-section {
  display: flex;
  gap: 2rem;
  align-items: end;
  padding: 0 2rem 2rem 2rem;
  max-width: 1400px;
  margin: 0 auto;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.filter-label {
  font-size: 0.9rem;
  color: #6c757d;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.filter-select {
  background: #ffffff;
  border: 2px solid #e9ecef;
  color: #495057;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-size: 0.95rem;
  min-width: 150px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.filter-select:focus {
  outline: none;
  border-color: #1C7AB1;
  box-shadow: 0 0 0 3px rgba(28, 122, 177, 0.1);
}

.refresh-btn {
  background: linear-gradient(45deg, #1C7AB1, #0d5a8a);
  border: none;
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(28, 122, 177, 0.2);
}

.refresh-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(28, 122, 177, 0.3);
}

.refresh-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.refresh-icon {
  font-size: 1.2rem;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* KPI Cards */
.kpi-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  padding: 0 2rem 2rem 2rem;
  max-width: 1400px;
  margin: 0 auto;
}

.kpi-card {
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 2rem;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}

.kpi-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #1C7AB1, #28a745);
}

.kpi-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  border-color: #1C7AB1;
}

.kpi-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.kpi-header h3 {
  margin: 0;
  font-size: 1rem;
  color: #6c757d;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.kpi-trend {
  font-size: 0.9rem;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
}

.kpi-trend.positive {
  color: #28a745;
  background: rgba(40, 167, 69, 0.1);
}

.kpi-trend.neutral {
  color: #ffc107;
  background: rgba(255, 193, 7, 0.1);
}

.kpi-value {
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 0.5rem;
  color: #1C7AB1;
}

.kpi-subtitle {
  color: #6c757d;
  font-size: 0.9rem;
}

/* Charts Section */
.charts-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
  gap: 2rem;
  padding: 0 2rem 2rem 2rem;
  max-width: 1400px;
  margin: 0 auto;
}

.chart-container {
  background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 2rem;
  min-height: 400px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}

.chart-container.full-width {
  grid-column: 1 / -1;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.chart-header h3 {
  margin: 0;
  font-size: 1.3rem;
  font-weight: 600;
  color: #ffffff;
}

.chart-controls {
  display: flex;
  gap: 0.5rem;
}

.chart-btn {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: rgba(255, 255, 255, 0.8);
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;
}

.chart-btn:hover {
  border-color: #1C7AB1;
  color: #1C7AB1;
  background: rgba(28, 122, 177, 0.1);
}

.chart-btn.active {
  background: #1C7AB1;
  border-color: #1C7AB1;
  color: #ffffff;
}

.chart-area {
  height: 300px;
  width: 100%;
}

/* Table Section */
.table-section {
  padding: 0 2rem 2rem 2rem;
  max-width: 1400px;
  margin: 0 auto;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.table-header h3 {
  margin: 0;
  font-size: 1.3rem;
  font-weight: 600;
  color: #333;
}

.export-btn {
  background: transparent;
  border: 2px solid #1C7AB1;
  color: #1C7AB1;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 600;
}

.export-btn:hover {
  background: #1C7AB1;
  color: #ffffff;
}

.table-container {
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}

.transactions-table {
  width: 100%;
  border-collapse: collapse;
}

.transactions-table th {
  background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: #ffffff;
  border-bottom: 1px solid #e9ecef;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-size: 0.85rem;
}

.transactions-table td {
  padding: 1rem;
  border-bottom: 1px solid #e9ecef;
  background: #ffffff;
}

.transactions-table tr:hover td {
  background: rgba(28, 122, 177, 0.05);
}

.transaction-id {
  font-family: 'Monaco', monospace;
  color: #1C7AB1;
  font-weight: 600;
}

.amount {
  font-weight: 700;
  color: #28a745;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
}

.status-badge.completed {
  background: rgba(40, 167, 69, 0.1);
  color: #28a745;
  border: 1px solid rgba(40, 167, 69, 0.2);
}

.status-badge.pending {
  background: rgba(255, 193, 7, 0.1);
  color: #ffc107;
  border: 1px solid rgba(255, 193, 7, 0.2);
}

.status-badge.failed {
  background: rgba(220, 53, 69, 0.1);
  color: #dc3545;
  border: 1px solid rgba(220, 53, 69, 0.2);
}

.action-btn {
  background: transparent;
  border: 1px solid #1C7AB1;
  color: #1C7AB1;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  font-weight: 600;
}

.action-btn:hover {
  background: #1C7AB1;
  color: #ffffff;
}

/* Responsive Design */
@media (max-width: 768px) {
  .dashboard-header {
    padding: 1rem;
  }
  
  .header-content {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
  
  .dashboard-title {
    font-size: 2rem;
  }
  
  .filters-section {
    padding: 0 1rem 1rem 1rem;
    gap: 1rem;
  }
  
  .kpi-section {
    grid-template-columns: 1fr;
    padding: 0 1rem 1rem 1rem;
  }
  
  .charts-section {
    grid-template-columns: 1fr;
    padding: 0 1rem 1rem 1rem;
  }
  
  .table-section {
    padding: 0 1rem 1rem 1rem;
  }
  
  .transactions-table {
    font-size: 0.9rem;
  }
  
  .transactions-table th,
  .transactions-table td {
    padding: 0.75rem 0.5rem;
  }
}
</style>